import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@common-fe/common-fe';

import { useSnackbar } from '@/modules/core/hooks';
import { GET_RENEWALS_QUERY_KEY } from '@/modules/Renewals/queries/useGetRenewals.query.ts';
import { RenewalStatus } from '@/modules/Renewals/renewals.types';
import { useUpdateRenewalSetupByIdQuery } from '@/modules/Renewals/RenewalSetup/queries';

export const useRenewalModals = () => {
  const mutation = useUpdateRenewalSetupByIdQuery();
  const { handleAddPermanentSnackbar } = useSnackbar();
  const [errorModal, setErrorModal] = useState(false);
  const [errorPlanYearId, setErrorPlanYearId] = useState<string | null>(null);
  const [visibleModals, setVisibleModals] = useState<Record<string, boolean>>({});
  const queryClient = useQueryClient();

  const openModal = useCallback((planYearId: string) => {
    setVisibleModals((prev) => ({ ...prev, [planYearId]: true }));
  }, []);

  const closeModal = useCallback((planYearId: string) => {
    setVisibleModals((prev) => ({ ...prev, [planYearId]: false }));
  }, []);

  const handleCompleteRenewal = useCallback(
    async (planYearId: string, withErrorIdReset = false) => {
      try {
        await mutation.mutateAsync({ id: planYearId, status: RenewalStatus.RESOLVED });
        handleAddPermanentSnackbar({
          text: 'Renewal request has been completed',
          closeIcon: true,
        });
        queryClient.invalidateQueries({
          queryKey: [GET_RENEWALS_QUERY_KEY],
        });
        closeModal(planYearId);
        if (withErrorIdReset) setErrorPlanYearId(null);
      } catch {
        setErrorPlanYearId(planYearId);
      }
    },
    [mutation, handleAddPermanentSnackbar, queryClient, closeModal]
  );

  useEffect(() => {
    if (mutation.isError) setErrorModal(true);
  }, [mutation.isError]);

  return {
    errorModal,
    setErrorModal,
    errorPlanYearId,
    setErrorPlanYearId,
    visibleModals,
    openModal,
    closeModal,
    handleCompleteRenewal,
  };
};
